'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Logomark } from '@/components/brand'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/context/LanguageContext'
import { useSession, signIn } from 'next-auth/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

interface NavItem {
  href: string
  labelKey: string
  icon: React.ElementType
}

interface NavSection {
  titleKey: string
  items: NavItem[]
}

interface NavLinkProps {
  href: string
  icon: React.ElementType
  children: React.ReactNode
  onClick?: () => void
}

function NavLink({ href, icon: Icon, children, onClick }: NavLinkProps) {
  const pathname = usePathname()
  const isActive = pathname === href

  return (
    <Link
      href={href}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-xl px-4 py-3',
        'text-base font-medium transition-all duration-200',
        isActive
          ? 'bg-brand-navy text-white'
          : 'text-brand-muted hover:bg-black/[0.04] hover:text-brand-navy'
      )}
    >
      <Icon
        strokeWidth={1.5}
        className={cn(
          'h-5 w-5 shrink-0',
          isActive ? 'text-white' : 'text-brand-muted'
        )}
      />
      <span>{children}</span>
    </Link>
  )
}

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const { t } = useLanguage()
  const { status } = useSession()
  const isAuthenticated = status === 'authenticated'

  const navItems: NavSection[] = [
    {
      titleKey: 'section.swaastha_ai',
      items: [
        { href: '/', labelKey: 'nav.recipe_generator', icon: Icons.sparkles },
        { href: '/nearby-centres', labelKey: 'nav.centres', icon: Icons.mapPin },
        { href: '/about', labelKey: 'nav.our_mission', icon: Icons.info },
        { href: '/developer-api', labelKey: 'nav.developer_api', icon: Icons.code },
      ],
    },
    ...(isAuthenticated
      ? [
          {
            titleKey: 'section.my_kitchen',
            items: [
              {
                href: '/ai/recipes/history',
                labelKey: 'nav.recipe_history',
                icon: Icons.clock,
              },
              {
                href: '/ai/recipes/saved',
                labelKey: 'nav.saved_recipes',
                icon: Icons.checkCircle,
              },
            ],
          },
        ]
      : []),
  ]

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side='left' className='w-72 p-0 md:hidden'>
        <SheetHeader className='flex h-20 flex-row items-center justify-between border-b border-black/[0.04] px-4'>
          <Link href='/' className='flex items-center gap-3' onClick={onClose}>
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-brand-navy shadow-soft'>
              <Logomark variant='light' size={24} />
            </div>
            <div className='flex flex-col'>
              <SheetTitle className='font-display text-base font-bold tracking-tight text-brand-navy'>
                {t('brand.title')}
              </SheetTitle>
              <span className='text-[10px] font-medium uppercase tracking-widest text-brand-muted'>
                {t('brand.tagline')}
              </span>
            </div>
          </Link>
        </SheetHeader>

        <nav className='flex-1 overflow-y-auto p-4' aria-label='Mobile navigation'>
          {navItems.map((section, sectionIndex) => (
            <div key={section.titleKey} className={cn(sectionIndex > 0 && 'mt-6')}>
              <p className='mb-2 px-4 text-[10px] font-semibold uppercase tracking-widest text-brand-muted/70'>
                {t(section.titleKey)}
              </p>
              <div className='space-y-1'>
                {section.items.map((item) => (
                  <NavLink
                    key={item.href}
                    href={item.href}
                    icon={item.icon}
                    onClick={onClose}
                  >
                    {t(item.labelKey)}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {!isAuthenticated && (
          <div className='border-t border-black/[0.04] p-4'>
            <Button
              onClick={() => signIn('keycloak')}
              variant='outline'
              className='w-full gap-2 border-brand-navy/15 text-brand-navy hover:bg-brand-cornflower/10 rounded-full'
            >
              <Icons.user className='h-4 w-4 text-brand-cornflower' />
              {t('auth.signin')}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
